import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useVideo } from '../context/VideoContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiCalendar, FiPlay, FiEye, FiTrendingUp } = FiIcons;

const Winners = () => {
  const { videos } = useVideo();
  const navigate = useNavigate();

  const [selectedMonthIdx, setSelectedMonthIdx] = useState(0);

  // Generate last 5 months
  const months = useMemo(() => {
    const result = [];
    const today = new Date();
    for (let i = 0; i < 5; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      result.push({
        label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        month: d.getMonth(),
        year: d.getFullYear(),
        date: d
      });
    }
    return result;
  }, []);

  // Filter and Sort Videos for Monthly Winners — ranked by Total Views
  const winningVideos = useMemo(() => {
    const target = months[selectedMonthIdx];
    if (!target) return [];

    return videos
      .filter(v => {
        if (!v.published_on) return false;
        const d = new Date(v.published_on);
        return d.getMonth() === target.month && d.getFullYear() === target.year;
      })
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 10);
  }, [videos, months, selectedMonthIdx]);

  // Hall of Fame — all time most viewed
  const hallOfFameVideos = useMemo(() => {
    return [...videos]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 10);
  }, [videos]);

  const navigateToProject = (video) => {
    navigate(`/project/${video.slug || video.id}`, {
      state: { from: '/winners', label: 'Back to Winners' }
    });
  };

  const getThumbnailUrl = (url) => {
    if (!url) return null;
    const youtubeRegex = new RegExp('(?:youtube\\.com/(?:[^/]+/.+/|(?:v|e(?:mbed)?)/|.*[?&]v=)|youtu\\.be/)([^"&?/\\s]{11})', 'i');
    const vimeoRegex = new RegExp('(?:vimeo\\.com/|player\\.vimeo\\.com/video/)([0-9]+)', 'i');

    const ytMatch = url.match(youtubeRegex);
    if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`;

    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;

    return null;
  };

  return (
    <div className="min-h-screen bg-[#09090b] pt-32 pb-20 px-6">
      <Helmet>
        <title>Winners & Hall of Fame | Video Examples</title>
        <meta name="description" content="Check out the most viewed videos of the month and our all-time Hall of Fame." />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-orange-500/30 mb-6"
          >
            <SafeIcon icon={FiAward} className="text-4xl text-amber-500" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl font-black text-white mb-6 uppercase tracking-tighter"
          >
            Monthly <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-600">Winners</span>
          </motion.h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            The most viewed videos curated from our collection each month.
          </p>
        </div>

        {/* Month Filter */}
        <div className="mb-16">
          <div className="flex flex-wrap justify-center gap-4">
            {months.map((m, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedMonthIdx(idx)}
                className={`px-6 py-4 rounded-2xl border transition-all text-sm font-bold uppercase tracking-widest ${
                  selectedMonthIdx === idx
                    ? 'bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-500/20 scale-105'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:border-white/30 hover:bg-white/10'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Winners List */}
        <div className="space-y-6 mb-32">
          <AnimatePresence mode="wait">
            {winningVideos.length > 0 ? (
              <motion.div
                key={selectedMonthIdx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 gap-6"
              >
                {winningVideos.map((video, index) => (
                  <div
                    key={video.id}
                    className="glass-card p-4 md:p-6 flex flex-col md:flex-row gap-6 items-center group hover:border-orange-500/30 transition-all relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 z-10 px-4 py-2 bg-white/10 backdrop-blur-md rounded-br-2xl text-4xl font-black text-white group-hover:text-orange-500 transition-colors shadow-lg border-b border-r border-white/5">
                      #{index + 1}
                    </div>

                    {/* Thumbnail */}
                    <div
                      className="w-full md:w-64 aspect-video rounded-xl overflow-hidden bg-zinc-900 relative flex-shrink-0 cursor-pointer"
                      onClick={() => navigateToProject(video)}
                    >
                      {getThumbnailUrl(video.video_link) ? (
                        <img src={getThumbnailUrl(video.video_link)} alt={video.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                      ) : (
                        <div className="absolute inset-0 bg-white/5" />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                          <SafeIcon icon={FiPlay} className="text-white fill-current" />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-center md:text-left min-w-0 pt-8 md:pt-0">
                      <h3
                        className="text-2xl font-bold text-white mb-2 truncate cursor-pointer hover:text-orange-500 transition-colors"
                        onClick={() => navigateToProject(video)}
                      >
                        {video.title}
                      </h3>

                      <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-slate-400 mb-4">
                        <span className="bg-white/5 px-2 py-1 rounded-lg border border-white/5 font-medium">{video.industry}</span>
                        {video.production && (
                          <span className="flex items-center gap-1.5 text-slate-300 font-bold">
                            <span className="w-1 h-1 rounded-full bg-slate-600 block"></span>
                            by
                            <Link
                              to={`/directory?name=${encodeURIComponent(video.production)}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-orange-400 transition-colors hover:underline decoration-orange-500/50 underline-offset-4"
                            >
                              {video.production}
                            </Link>
                          </span>
                        )}
                      </div>

                      {/* View Count */}
                      <div className="flex items-center justify-center md:justify-start gap-2 px-3 py-1.5 rounded-xl bg-white/5 border border-white/5 w-fit">
                        <SafeIcon icon={FiEye} className="text-slate-500" />
                        <span className="text-slate-300 font-bold font-mono text-sm">{video.views || 0}</span>
                        <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Views</span>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="hidden md:block">
                      <button
                        onClick={() => navigateToProject(video)}
                        className="btn-secondary whitespace-nowrap"
                      >
                        View Project
                      </button>
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10"
              >
                <SafeIcon icon={FiCalendar} className="text-4xl text-white/20 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Winners Yet</h3>
                <p className="text-slate-500">No videos found for {months[selectedMonthIdx]?.label}.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Hall of Fame Section */}
        <div className="pt-20 border-t border-white/5">
          <div className="flex flex-col md:flex-row items-end justify-between mb-12 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiTrendingUp} className="text-orange-500" />
                <h3 className="text-sm font-bold text-orange-500 uppercase tracking-widest">All Time Best</h3>
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight">Hall of <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">Fame</span></h2>
            </div>
            <p className="text-slate-400 max-w-md text-right md:text-left">
              The most watched videos in our collection history. These projects have stood the test of time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {hallOfFameVideos.map((video, idx) => (
              <motion.div
                key={video.id}
                className="group bg-zinc-900/50 border border-white/5 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => navigateToProject(video)}
              >
                <div className="aspect-video bg-zinc-800 relative overflow-hidden">
                  <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 backdrop-blur rounded-lg text-xs font-black text-white z-10 border border-white/10">
                    #{idx + 1}
                  </div>
                  {getThumbnailUrl(video.video_link) && (
                    <img src={getThumbnailUrl(video.video_link)} alt={video.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
                  <div className="absolute bottom-3 right-3 text-xs font-bold text-white flex items-center gap-1">
                    <SafeIcon icon={FiEye} /> {video.views}
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-sm font-bold text-white truncate mb-1 group-hover:text-orange-400 transition-colors">{video.title}</h4>
                  <p className="text-xs text-slate-500 truncate">{video.production || video.client}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Winners;